import type {DragEndEvent} from '@dnd-kit/react';
import {isSortable} from '@dnd-kit/dom/sortable';

import logger from 'src/utils/logger';
import {useDashboardStore} from 'src/components/analytics-genesis/byod/store/dashboardStore';

import {ROW_END_SUFFIX, type DragEndResult} from './handle-drag-end';


type DndSourceData = {type?: string; rowId?: string; label?: string};

function isDndSourceData(data: unknown): data is DndSourceData {
  if (data == null || typeof data !== 'object') {
    return false;
  }
  const d = data as Record<string, unknown>;
  return (
    (d.type === undefined || typeof d.type === 'string') &&
    (d.rowId === undefined || typeof d.rowId === 'string')
  );
}

/**
 * Translate an `@dnd-kit/react` `DragEndEvent` into the shape
 * `decideDragOutcome` operates on. Returns `null` when there's no source
 * entity to act on (e.g. a stray fire from the manager).
 *
 * Why a separate helper: the conversion is ~50 lines of branching over
 * sortable vs plain droppable target, plus a row-end-sentinel resolution
 * step that needs the live store to look up the parent row's tile count.
 * Inlining it in `onDragEnd` made the canvas component hard to read and
 * mixed pure event-shape work with React-state cleanup.
 */
export function buildDragEndResult(event: DragEndEvent): DragEndResult | null {
  const src = event.operation.source;
  if (!src) {
    return null;
  }

  const draggableId = String(src.id);

  let sourceDroppableId = '';
  let sourceIndex = 0;
  if (isSortable(src)) {
    sourceDroppableId = String(src.sortable.initialGroup ?? src.id);
    sourceIndex = src.sortable.initialIndex;
  } else {
    // Defensive: every BYOD tile uses `useSortable`, so the source SHOULD
    // always be sortable. If a future change introduces a non-sortable
    // draggable, fall back to the `rowId` carried in the source's data so
    // the store's `moveTile` doesn't silently no-op on `state.rows['']`.
    const srcData = isDndSourceData(src.data) ? src.data : undefined;
    sourceDroppableId = srcData?.rowId ?? String(src.id);
    sourceIndex = 0;
    logger.warn('buildDragEndResult: source is not sortable', {
      id: String(src.id),
      rowId: srcData?.rowId,
    });
  }

  const target = event.operation.target;
  let destination: DragEndResult['destination'] = null;
  if (target) {
    if (isSortable(target)) {
      const destDroppableId = String(target.sortable.group ?? target.id);
      let destIndex = target.sortable.index;
      if (destDroppableId === sourceDroppableId && sourceIndex < destIndex) {
        destIndex -= 1;
      }
      destination = {
        droppableId: destDroppableId,
        index: destIndex,
      };
    } else {
      const targetId = String(target.id);
      // Row-end sentinel ids end with ROW_END_SUFFIX. Resolve the parent
      // row id and use its current tile count as the "append" index so a
      // drop on the empty area to the right of the last tile lands at the
      // end of the row, not at index 0.
      if (targetId.endsWith(ROW_END_SUFFIX)) {
        const parentRowId = targetId.slice(0, -ROW_END_SUFFIX.length);
        const parentRow = useDashboardStore.getState().rows[parentRowId];
        destination = {
          droppableId: parentRowId,
          index: parentRow ? parentRow.tiles.length : 0,
        };
      } else {
        destination = {droppableId: targetId, index: 0};
      }
    }
  }

  // Row-type lookup so `decideDragOutcome` can reject cross-type drops
  // even when collision detection still produced a destination.
  const currentRows = useDashboardStore.getState().rows;
  const rowTypes: Record<string, string> = {};
  for (const [rid, r] of Object.entries(currentRows)) {
    rowTypes[rid] = r.type;
  }

  const srcDataFinal = isDndSourceData(src.data) ? src.data : undefined;
  return {
    draggableId,
    source: {droppableId: sourceDroppableId, index: sourceIndex},
    destination,
    reason: event.canceled ? 'CANCEL' : 'DROP',
    dragType: srcDataFinal?.type,
    rowTypes,
  };
}
